import React from "react";
import { C } from "../reporting/reportingStyles";
import { ROUTING_MODES } from "./deviceRouting";

export const formatCustomDateTime = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  const day = date.getDate();
  const month = date.toLocaleString("en-US", { month: "short" });
  const year = date.getFullYear();
  let hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const seconds = date.getSeconds().toString().padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  hours = hours ? hours : 12;
  return `${day} ${month} ${year}, ${hours}:${minutes}:${seconds} ${ampm}`;
};

export const getTimeAgo = (dateString) => {
  if (!dateString) return "N/A";
  const diffInSeconds = Math.floor((new Date() - new Date(dateString)) / 1000);
  if (diffInSeconds < 60) return `${diffInSeconds} second${diffInSeconds !== 1 ? "s" : ""} ago`;
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) return `${diffInMinutes} minute${diffInMinutes !== 1 ? "s" : ""} ago`;
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `${diffInHours} hour${diffInHours !== 1 ? "s" : ""} ago`;
  const diffInDays = Math.floor(diffInHours / 24);
  return `${diffInDays} day${diffInDays !== 1 ? "s" : ""} ago`;
};

export const StatusPill = ({ connected, offlineLogin }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700, background: connected ? C.greenLight : C.coralLight, color: connected ? C.green : C.coral }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: connected ? C.green : C.coral }} />
      {connected ? "Online" : "Offline"}
    </span>
    {offlineLogin && <span style={{ padding: "4px 8px", borderRadius: 20, fontSize: 10, fontWeight: 700, background: C.orangeLight, color: C.orange }}>Offline Login</span>}
  </div>
);

const KEY_LOOKS = {
  active: { label: "Own key", background: C.greenLight, color: C.green },
  revoked: { label: "Key revoked", background: C.coralLight, color: C.coral },
  none: { label: "Company key", background: C.bg, color: C.textMed },
};

// A tablet's own POS key, from fetchPosDevices' keyStatus / keyPendingRequest.
export const KeyStatusPill = ({ status, pending, pendingOnly = false }) => {
  const look = KEY_LOOKS[status] || KEY_LOOKS.none;
  const pill = { padding: "4px 8px", borderRadius: 20, fontSize: 10, fontWeight: 700, whiteSpace: "nowrap" };
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      {!pendingOnly && <span style={{ ...pill, background: look.background, color: look.color }}>{look.label}</span>}
      {pending && <span style={{ ...pill, background: C.orangeLight, color: C.orange }}>Key approval pending</span>}
    </span>
  );
};

export const DetailRow = ({ icon, label, value, iconColor }) => (
  <div style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "10px 0", borderBottom: `1px solid ${C.borderLight}` }}>
    <div style={{ width: 36, height: 36, borderRadius: 10, background: C.coralLight, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      <i className={`fa fa-${icon}`} style={{ color: iconColor || C.coral, fontSize: 14 }} />
    </div>
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, color: C.textLight, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginTop: 2 }}>{value || "N/A"}</div>
    </div>
  </div>
);

const MODE_COLORS = {
  channel: { color: C.coral, bg: C.coralLight },
  primary: { color: C.blue, bg: C.blueLight },
  broadcast: { color: C.textMed, bg: C.bg },
};

export const RoutingModeBadge = ({ mode, short = false }) => {
  const meta = ROUTING_MODES[mode] || ROUTING_MODES.broadcast;
  const colors = MODE_COLORS[mode] || MODE_COLORS.broadcast;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 9px", borderRadius: 20, fontSize: 10, fontWeight: 700, color: colors.color, background: colors.bg, border: `1px solid ${colors.bg === C.bg ? C.border : colors.bg}`, whiteSpace: "nowrap" }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: colors.color }} />
      {short ? meta.short : meta.label}
    </span>
  );
};

export const ChannelChip = ({ name, muted }) => (
  <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 6, fontSize: 11, fontWeight: 600, color: muted ? C.textLight : C.coralDark, background: muted ? C.bg : C.coralLight, border: `1px solid ${muted ? C.border : C.coralLight}`, marginRight: 4, marginBottom: 4, textDecoration: muted ? "line-through" : "none" }}>
    {name}
  </span>
);

// A titled block inside the device modal.
export const Section = ({ title, hint, right, children, style }) => (
  <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 12, padding: 16, marginBottom: 16, ...style }}>
    {(title || right) && (
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: hint ? 4 : 12 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{title}</div>
        {right}
      </div>
    )}
    {hint && <div style={{ fontSize: 12, color: C.textMed, marginBottom: 12, lineHeight: 1.5 }}>{hint}</div>}
    {children}
  </div>
);

export const Notice = ({ tone = "warn", children }) => {
  const tones = {
    warn: { color: "#9a5b00", bg: C.orangeLight, border: "#fde0b0" },
    info: { color: C.textMed, bg: C.bg, border: C.border },
    ok: { color: "#1d8a4f", bg: C.greenLight, border: "#c9f2da" },
  };
  const t = tones[tone] || tones.info;
  return (
    <div style={{ fontSize: 12, fontWeight: 500, lineHeight: 1.5, padding: "8px 12px", borderRadius: 8, color: t.color, background: t.bg, border: `1px solid ${t.border}`, marginTop: 8 }}>
      {children}
    </div>
  );
};
